import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';

import { appeler } from '../../api/client.ts';
import { Erreur } from '../../composants/communs.tsx';
import { Sheet } from '../../composants/ui/index.ts';
import type { Produit } from './types.ts';

/**
 * Identifiant SERVEUR du produit.
 *
 * La liste du catalogue renvoie un identifiant par CANAL : le même produit y
 * figure une fois au détail, une fois au gros. Le seuil, lui, porte sur le
 * produit — l'envoyer avec le canal collé ferait répondre 404.
 */
function produitServeur(produit: Produit): string {
  return produit.id.split('|')[0] ?? produit.id;
}

/**
 * Seuil de réapprovisionnement — RG-186.
 *
 * Trois états que l'écran doit rendre distincts, parce qu'ils disent des
 * choses différentes :
 *
 * - champ VIDE      → aucun seuil, donc aucune alerte sur ce produit ;
 * - seuil à ZÉRO    → alerte désactivée là où elle est posée (RG-186a) ;
 * - seuil à N       → alerte quand le stock descend à N.
 *
 * Les confondre ferait taire des alertes qu'on croyait actives, ou l'inverse.
 */
export function FormulaireSeuil({
  produit,
  boutiqueId,
  onFerme,
  onEnregistre,
}: {
  produit: Produit;
  /** `null` = on règle le seuil COMMUN ; sinon la surcharge de cette boutique. */
  boutiqueId: string | null;
  onFerme: () => void;
  onEnregistre: () => void;
}): ReactNode {
  const [valeur, setValeur] = useState(
    produit.seuilReapprovisionnement === undefined
      ? ''
      : String(produit.seuilReapprovisionnement),
  );

  const enregistrer = useMutation({
    mutationFn: async () => {
      const seuil = valeur.trim() === '' ? null : Number(valeur);

      if (boutiqueId === null) {
        // Seuil commun : porté par le produit lui-même.
        return appeler(`/produits/${produitServeur(produit)}`, {
          methode: 'PATCH',
          corps: { seuil_reapprovisionnement: seuil },
        });
      }

      // Surcharge locale. Chemin distinct de `/seuils`, qui porte le seuil de
      // QUANTITÉ d'un canal (RG-034) et n'a rien à voir.
      return appeler(
        `/produits/${produitServeur(produit)}/seuil-reapprovisionnement`,
        {
          methode: 'POST',
          corps: { boutique_id: boutiqueId, seuil },
        },
      );
    },
    onSuccess: () => {
      onEnregistre();
      onFerme();
    },
  });

  return (
    <Sheet titre={`Seuil de réapprovisionnement — ${produit.designation}`}>
      <p className="aide">
        {boutiqueId === null
          ? 'Seuil commun à toutes les boutiques. Une boutique peut le surcharger.'
          : 'Surcharge pour cette boutique. Vider le champ rend la main au seuil commun.'}
      </p>

      <div className="champ">
        <label htmlFor="seuil">Alerter quand le stock descend à</label>
        <input
          id="seuil"
          type="number"
          min={0}
          value={valeur}
          placeholder="aucune alerte"
          onChange={(evenement) => {
            setValeur(evenement.target.value);
          }}
        />
        <p className="aide">
          Laisser vide : aucune alerte sur ce produit. Zéro : alerte
          désactivée, ce qui n’est pas la même chose qu’une alerte à la
          rupture.
        </p>
      </div>

      <Erreur erreur={enregistrer.error} />

      <div className="ligne">
        <button
          type="button"
          onClick={() => {
            enregistrer.mutate();
          }}
          disabled={enregistrer.isPending}
        >
          Enregistrer
        </button>
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Annuler
        </button>
      </div>
    </Sheet>
  );
}
